/**
 * Sign out from the browser, clearing what this browser holds for the viewer
 * first (#14375).
 *
 * The recording chunk buffer (`@/lib/notetaker/chunk-buffer`) keeps meeting
 * audio that has not reached the server yet. Left behind at sign-out, it is a
 * room's conversation readable in DevTools by whoever uses this browser next,
 * so every sign-out control goes through here rather than calling
 * `next-auth/react`'s `signOut` directly.
 *
 * The clear is BOUNDED by {@link SIGN_OUT_CLEAR_TIMEOUT_MS} and cannot fail
 * the sign-out: a browser whose IndexedDB hangs or refuses still signs out,
 * and the buffer's own age limit clears what is left.
 *
 * Client-only.
 */

import { signOut } from 'next-auth/react'

import { clearRecordingChunkBuffer } from '@/lib/notetaker/chunk-buffer'

/** The most sign-out waits for the buffer to clear — one second. */
export const SIGN_OUT_CLEAR_TIMEOUT_MS = 1_000

type SignOutOptions = NonNullable<Parameters<typeof signOut>[0]>

export async function signOutAndClearBrowserData(
  options: SignOutOptions,
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      clearRecordingChunkBuffer().catch(() => undefined),
      new Promise<void>(resolve => {
        timer = setTimeout(resolve, SIGN_OUT_CLEAR_TIMEOUT_MS)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
  await signOut(options)
}
